import { useCallback, useEffect, useState } from 'react';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getMemberById } from 'src/shared/services/memberService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { Box, Button, IconButton, Typography } from '@mui/material';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { Trans, useTranslation } from 'react-i18next';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useNavigate } from 'react-router-dom';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { useGlobals } from 'src/shared/contexts/appContext';
import { Member } from '../models/member';
import { MembershipPartial } from './membershipPartial';
import { MemberDetailsPresentation } from './memberDetailsPresentation';

type MemberDetailModalProps = {
  otherContactMode: boolean;
  memberId: number;
  currentMemberIds: number[];
  onClose: () => void;
  changeMemberId: (newMemberId: number) => void;
};

export const MemberDetailModal = (props: MemberDetailModalProps) => {
  const { otherContactMode, memberId, currentMemberIds, onClose, changeMemberId } = props;
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const isMounted = useIsMounted();

  const { currentUser } = useGlobals();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [member, setMember] = useState<Member>();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await getMemberById(memberId);
        if (isMounted()) setMember(result.data);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setIsLoading(false);
      }
    };

    fetchData();
  }, [isMounted, memberId]);

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('member:otherContact.label'));

  const step = (forward: boolean): number => {
    const currentIndex = currentMemberIds.indexOf(memberId);

    if (currentIndex === -1) {
      // If the currentId is not found in the array
      return memberId;
    }

    if (forward) {
      // Move forward: Check if there's a next element
      if (currentIndex < currentMemberIds.length - 1) {
        return currentMemberIds[currentIndex + 1];
      }
      // Wrap around to the first element
      return currentMemberIds[0];
    }
    // Move backward: Check if there's a previous element
    if (currentIndex > 0) {
      return currentMemberIds[currentIndex - 1];
    }
    // Wrap around to the last element
    return currentMemberIds[currentMemberIds.length - 1];
  };

  const stepMemberIds = (forward: boolean) => {
    changeMemberId(step(forward));
  };

  const getDialogTitle = () => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={() => stepMemberIds(false)} color='primary' disabled={!member} size='large'>
          <KeyboardArrowLeft fontSize='large' />
        </IconButton>
        <Typography sx={{ mx: 2 }} variant='h6' component='div'>
          {currentMemberIds && (
            <Trans
              i18nKey='member:memberDetailModalHeader'
              values={{
                label: otherContactMode ? otherLabel : t('member:memberLabel'),
                count: currentMemberIds.indexOf(memberId) + 1,
                totalCount: currentMemberIds.length,
              }}
            />
          )}
        </Typography>
        <IconButton onClick={() => stepMemberIds(true)} color='primary' disabled={!member} size='large'>
          <KeyboardArrowRight fontSize='large' />
        </IconButton>
      </Box>
    );
  };

  return (
    <GenericDialog
      open
      maxWidth='sm'
      padding={0}
      titlePadding={0.5}
      dialogTitle={getDialogTitle()}
      dialogActions={
        <Box sx={{ display: 'flex' }}>
          <Button
            variant='outlined'
            sx={{ mr: 1 }}
            disableElevation
            onClick={() => {
              navigate(`${RoutesEnum.MemberDetails}/${memberId}`);
            }}
          >
            {t('common:button.profilePage')}
          </Button>
          <Button variant='outlined' onClick={onHandleClose}>
            {t('common:button.close')}
          </Button>
        </Box>
      }
      onClose={onHandleClose}
      onSave={onHandleClose}
      dialogContent={
        <Box sx={{ p: 2, position: 'relative', minHeight: '10rem' }}>
          <LoadingOverlay loading={isLoading} />
          {member && (
            <Box sx={{ mb: 3 }}>
              <MemberDetailsPresentation member={member} />
            </Box>
          )}
          {member && (
            <MembershipPartial
              otherContactMode={otherContactMode}
              member={member}
              showEditButtons={false}
              refetchMemberCallback={() => ConsoleHelper.log('x')}
            />
          )}
        </Box>
      }
    />
  );
};
